import React, { ReactElement } from 'react';
import { useTranslate } from 'hooks/translate';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import './guest-information.scss';
import { useReservations } from 'contexts/reservations-context';
import TabNavigation from 'components/tabs/tab-navigation';
import PassengerForm from 'components/passenger-card/passenger-form/passenger-form';
import { NORDIC_COUNTRIES } from 'constants/countries';
import { formatPassengerCards } from 'helpers/passenger-helpers';
import { dateTransform } from 'helpers/date-helper';

interface GuestInformationProps {
  onNext: () => void;
  onPrevious?: () => void;
  user?: Customer;
}

export const passengerDeclarationsDefault: PassengerDeclaration = {
  firstName: '',
  lastName: '',
  dateOfBirth: null,
  dateOfArrival: null,
  nationality: '',
  address: '',
  countryOfEntry: 'fi',
  passportNumber: '',
  passengerCompanion: [],
};

const GuestInformation = ({
  user,
  onNext,
  onPrevious,
}: GuestInformationProps): ReactElement => {
  const { t } = useTranslate();

  const { passengerDeclarations, setPassengerDeclarations } = useReservations();

  const passengerSchema = yup.object().shape({
    passengers: yup.array<PassengerDeclaration[]>().of(
      yup.object<PassengerDeclaration>().shape({
        firstName: yup.string().required(),
        lastName: yup.string().required(),
        dateOfBirth: yup
          .string()
          .transform(dateTransform)
          .required(),
        nationality: yup.string().required(),
        address: yup.string().required(),
        countryOfEntry: yup.string().required(),
        passportNumber: yup.string().when('countryOfEntry', {
          is: (value) => !NORDIC_COUNTRIES.includes(value),
          then: yup.string().required(),
        }),
        dateOfArrival: yup
          .string()
          .nullable()
          .when('countryOfEntry', {
            is: (value) => !NORDIC_COUNTRIES.includes(value),
            then: yup
              .string()
              .transform(dateTransform)
              .required(),
          }),
        passengerCompanion: yup.array<PassengerCompanion[]>().of(
          yup.object<PassengerDeclaration>().shape({
            firstName: yup.string().required(),
            lastName: yup.string().required(),
            dateOfBirth: yup
              .string()
              .transform(dateTransform)
              .required(),
          }),
        ),
      }),
    ),
  });

  const form = useForm<PassengerDeclarationValues>({
    resolver: yupResolver(passengerSchema),
    mode: 'all',
    reValidateMode: 'onBlur',
    defaultValues: {
      passengers:
        passengerDeclarations.length !== 0
          ? formatPassengerCards(passengerDeclarations)
          : [passengerDeclarationsDefault],
    },
  });

  const {
    control,
    register,
    handleSubmit,
    clearErrors,
    formState,
    getValues,
  } = form;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'passengers',
  });

  const onSubmit = (data: PassengerDeclarationValues): void => {
    setPassengerDeclarations(data.passengers);
    onNext();
  };

  const { errors } = formState;

  const renderPassengerForms = fields.map(
    (passenger, key): ReactElement => {
      const namePrefix = `passengers[${key}]`;
      const formErrors = errors.passengers?.[key]
        ? errors.passengers[key]
        : undefined;
      return (
        <PassengerForm
          key={passenger.id}
          errors={formErrors}
          index={key}
          control={control}
          registerRef={register}
          namePrefix={namePrefix}
          onDelete={() => remove(key)}
          onChange={() => clearErrors()}
          countryOfEntry={passengerDeclarations[key]?.countryOfEntry ?? 'fi'}
          getValues={getValues}
          formState={formState}
        />
      );
    },
  );

  return (
    <div className='guest-information-container'>
      <div className='subHeader'>
        <h1 className='white'>{t('reservation.guestInformation.header')}</h1>
        <div className='subheader'>
          <p className='white'>{t('reservation.guestInformation.subHeader')}</p>
        </div>
      </div>
      <div className='guest-information'>
        <form onSubmit={handleSubmit(onSubmit)}>{renderPassengerForms}</form>
        <div className='center'>
          <button
            className='add-button'
            onClick={() => append(passengerDeclarationsDefault)}
          >
            {t('reservation.guestInformation.addPassenger')}
          </button>
        </div>
      </div>
      <TabNavigation
        onContinue={handleSubmit(onSubmit)}
        onPrevious={onPrevious}
        hasPrevious
        previousText={t('reservation.previous')}
        continueText={t('reservation.continue')}
      />
    </div>
  );
};

export default GuestInformation;
